import ButtonComponent from "../../../components/common/button/Button";
import Modal from "../../../components/common/modal/Modal";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import dayjs from 'dayjs';
import DatePicker from "../../../components/common/DatePicker";
import Dropdown from "../../../components/common/Dropdown"
import { useDispatch, useSelector } from "react-redux";
import { getForecastHistoryTableEndpoint } from "../../../common/endpoints";
import { getForecastHistoryTableSlice } from "../../../redux/actions/ForecastHistoryTableSlice";
import { Loader } from "../../../components/common/Loader";
import './HistoryDataTable.css';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';

const HistoryDataTable = ({ openHistoryModal, historySelectedRowData, handleCloseShowHistoryModal, selectedStartingFrom, selectedViewBy }) => {
  const onCloseHistoryModal = () => {
    handleCloseShowHistoryModal();
  }
  const onClickCancel = () => {
    handleCloseShowHistoryModal();
  }

  return (
    <div>
      <Modal
        className="showVariantModal_cls"
        open={openHistoryModal}
        handleClose={onCloseHistoryModal}
        title="Forecast History"
        fullWidth={true}
        maxWidth={"lg"}
        action={
          <ForecastHistoryFooter
            onClickCancel={onClickCancel}
          />
        }
        content={
          <ForecastHistoryContent
            onClickCancel={onClickCancel}
            historySelectedRowData={historySelectedRowData}
            selectedStartingFromData={selectedStartingFrom}
            selectedViewByData={selectedViewBy}
          />
        }
      />
    </div>
  )
}

const ForecastHistoryFooter = ({ onClickCancel }) => {
  return (
    <div className='finalLTupdatedfooter-btn-wrapper'>
      <ButtonComponent
        label="Close"
        type="primary"
        maxWidth="80px"
        onClick={onClickCancel}
      />
    </div>
  );
};

const ForecastHistoryContent = ({ historySelectedRowData, selectedStartingFromData, selectedViewByData }) => {
  const [rowData, setRowData] = useState([]);
  const [selectedViewBy, setSelectedViewBy] = useState(selectedViewByData);
  const [selectedStartingFrom, setSelectedStartingFrom] = useState();
  const [forecastHistoryData, setForecastHistoryData] = useState([]);
  const { supplierId, tcccMaterialNumber, plantCode, businessCategory } = historySelectedRowData;

  const [selectedViewByOptions, setSelectedViewByOptions] = useState([]);
  const [gridApi, setGridApi] = useState();
  const stateSelectorHistoryTableData = useSelector((state) => state.ForecastHistoryTableSlice);
  const selectedRegion = useSelector((state) => state.setRegionPlantStore.selectedRegion);
  
  const dispatch = useDispatch();

  useEffect(() => {
    getForecastHistoryTable(selectedStartingFrom);
  }, [selectedViewBy]);

  const getForecastHistoryTable = async (startDate) => {
    const loggedInUserEmail = localStorage.getItem("userEmail");
    const userRole = localStorage.getItem("userRole"); // ✅ added
    let formattedDate = "";

    if (startDate) {
      const dayjsObj = dayjs(startDate);
      formattedDate = `${dayjsObj.year()}-${String(dayjsObj.month() + 1).padStart(2, "0")}-${String(dayjsObj.date()).padStart(2, "0")}`;
    }

    try {
      const responseSupplierForecastHistoryData = await dispatch(
        getForecastHistoryTableSlice({
          dynamicEndpoint: `${getForecastHistoryTableEndpoint}/${loggedInUserEmail}`,
          params: {
            supplier: supplierId.split(' | ')[0],
            material: tcccMaterialNumber,
            plant: plantCode,
            businessCategory: businessCategory,
            startDate: formattedDate,
            viewBy: selectedViewBy,

            // 👉 only extra field added conditionally
            ...(["Regional Planner", "Regional Viewer"].includes(userRole) && {
              region: selectedRegion
            })
          }
        })
      );

      if (responseSupplierForecastHistoryData?.meta?.requestStatus === 'fulfilled') {
        const list = responseSupplierForecastHistoryData?.payload;
        setForecastHistoryData(list.forecast);
        setSelectedViewByOptions(list.viewByOptions);
        setSelectedViewBy(list.viewBy);
        setSelectedStartingFrom(dayjs(list.startDate));
      }
    } catch (error) {
      console.error('API error:', error);
    }
  };

  const handleViewByDropdown = (event) => {
    setSelectedViewBy(event.target.value);
  };

  const handleDateChange = (newDate) => {
    // setSelectedStartingFrom(newDate);
    getForecastHistoryTable(newDate);
  };
  // Transform JSON into rowData
  useEffect(() => {
    if (forecastHistoryData && forecastHistoryData.length > 0) {
      const transformed = forecastHistoryData.flatMap((monthData, groupIndex) => {
        return monthData.timeline.map((figure, index) => {
          const row = {
            month: monthData.month,
            keyFigure: figure.keyFigureName, // FIXED casing
            _groupIndex: groupIndex,
            _rowIndexInGroup: index,
          };

          // Copy all week/date keys dynamically
          Object.keys(figure).forEach(key => {
            if (key !== "keyFigureName") {
              row[key] = figure[key] ?? "";
            }
          });

          return row;
        });
      });
      setRowData(transformed);
    }
  }, [forecastHistoryData]);

  const rowSpanFn = useCallback((params) => {
    if (!params.data) return 1;
    return params.data._rowIndexInGroup === 0 ? params.data._rowCountInGroup : 1;
  }, []);

  // Dynamically generate columns from JSON keys
  const weekKeys = forecastHistoryData[0]?.timeline
    ? Object.keys(forecastHistoryData[0].timeline[0]).filter(k => k !== "keyFigureName")
    : [];

  const weekColumns = weekKeys.map(key => ({
    headerName: key,
    field: key,
    width: 180,
    wrapText: true,
    autoHeight: true,
    cellClass: "wrap-cell",
    valueGetter: (params) => params.data[key] ?? "",
  }));

  const columnDefs = [
    {
      headerName: "Month",
      field: "month",
      pinned: 'left',
      lockPinned: true,
      lockPosition: true,
      width: 200,
      wrapText: true,
      autoHeight: true,
      cellClass: "wrap-cell",
      rowSpan: rowSpanFn,
      cellRenderer: (params) =>
        params.data._rowIndexInGroup === 0 ? params.value : "",
    },
    {
      headerName: "Key Figures", field: "keyFigure", wrapText: true,
      autoHeight: true,
      cellClass: "wrap-cell",
      pinned: 'left',
      lockPinned: true,
      lockPosition: true,
    },
    ...weekColumns,
  ];

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agMultiColumnFilter"
    }),
    []
  );

  const onGridReady = (params) => {
    setGridApi(params?.api);
    // gridRef.current = params.api;
  }

  const onClickDownloadButton = async () => {
    // gridApi?.exportDataAsExcel({
    //   fileName: "history-table-data.xlsx",
    //   sheetName: "My Sheet",
    // });
    const excelData = gridApi.getDataAsExcel({
      fileName: "history-table-data",
      sheetName: "history-table-data"
    });

    // 2️⃣ Load into ExcelJS
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(excelData);

    const worksheet = workbook.worksheets[0];

    // ✅ Freeze first two columns (no data touched)
    worksheet.views = [
      {
        state: "frozen",
        xSplit: 2,
        ySplit: 1,
        topLeftCell: "C2"
      }
    ];

    // 3️⃣ Download again
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "history-table-data.xlsx");
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 5,
          fontSize: 12,
          marginTop: 13,
          paddingRight: 30,
        }}
      >
        {/* LEFT SIDE – Download */}
        <div style={{ display: 'flex', alignItems: 'center' }}>

          <ButtonComponent
            type="primary"
            maxWidth="100px"
            label="Download"
            height="30px"
            onClick={onClickDownloadButton}
            disabled={rowData.length === 0}
          />

        </div>

        {/* RIGHT SIDE – Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
          }}>
            <p style={{ fontSize: 15 }}>View By:</p>
            <Dropdown
              height="30px"
              fontSize="14px"
              sx={{
                fontSize: '12px',
                fontWeight: 'normal',
                '& .MuiInputBase-root': {
                  fontSize: '12px',
                  fontWeight: 'normal',
                },
              }}
              maxWidth="100%"
              value={selectedViewBy}
              name="selectedViewBy"
              options={
                selectedViewByOptions?.length
                  ? selectedViewByOptions.map(option => ({
                    label: option,
                    value: option,
                  }))
                  : [
                    { label: 'Weekly', value: 'Weekly' },
                    { label: 'Monthly', value: 'Monthly' },
                    { label: 'Yearly', value: 'Yearly' },
                  ]
              }
              handleChange={handleViewByDropdown}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <p style={{ margin: 0, fontSize: 15 }}>Starting from:</p>
            <div style={{ width: 120 }}>
              <DatePicker
                name="Starting From"
                value={selectedStartingFrom}
                onChange={handleDateChange}
                height="30px"
                width={150}
                fontSize={13}
                fontWeight="normal"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        {!stateSelectorHistoryTableData.loading ? <div className="ag-theme-quartz" style={{ height: 350, width: "100%" }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            enableCellTextSelection={true}
            pagination={true}
            defaultColDef={defaultColDef}
            paginationPageSize={100}
            onGridReady={onGridReady}
            paginationPageSizeSelector={[50, 100, 200]}
          />
        </div> : <Loader />}
      </div>
    </div>
  );
};

export default HistoryDataTable;